/* eslint-disable react/jsx-no-bind */
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from 'Component/Button';
import {
    SIZE_NONE,
    TEXT_SIZE_SMALL,
    TYPE_TERTIARY,
    TYPE_TEXT,
    WIDTH_FULL,
    WIDTH_HUG
} from 'Component/Button/Button.config';
import CheckMarkFilledIcon from 'Component/CheckMarkFilledIcon';
import {
    EVENT_GTM_PRODUCTCLICK_POPUP,
    EVENT_GTM_SCROLL_POPUP
} from 'Component/GoogleTagManager/GoogleTagManager.events';
import Image from 'Component/Image';
import Label from 'Component/Label';
import {
    COLOR_WHITE_AND_RED, PADDING_LARGE
} from 'Component/Label/Label.config';
import LabelContainer from 'Component/LabelContainer';
import { TOP_LC_S } from "Component/LabelContainer/LabelContainer.config";
import { PAGE_TYPE_CART } from 'Component/Meta/Meta.config';
import Popup from 'Component/Popup';
// import ProductLabels from "Component/ProductLabels";
import ProductPrice from 'Component/ProductPrice';
import withLayoutPlugins from 'LayoutPlugins/hoc/withLayoutPlugins';
import { CROSS_SELL, RELATED } from 'Store/LinkedProducts/LinkedProducts.reducer';
import { ProductType } from 'Type/ProductList.type';
import fromCache from 'Util/Cache/Cache';
import { lazyComponentLoader } from 'Util/lazyComponentLoader';
import {
    getPrice
} from 'Util/Product/Extract';
import PRODUCT_TYPE from 'Util/Product/Product.config';

import { ADDED_PRODUCT_POPUP_ID, SHOW_PAIR_OPTION } from './AddToCartPopUp.config';
const ProductLinks = lazyComponentLoader(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-links" */
    'Component/ProductLinks'
), 2);
const FreeShippingNotification = lazyComponentLoader(() => import(
    /* webpackMode: "lazy", webpackChunkName: "free-shipping-notification" */
    'Component/FreeShippingNotification'
), 2);

import './AddtoCartPopUp.plugin';
/** @namespace Bodypwa/Component/AddToCartPopUp/Component/AddToCartPopUp */
export function AddToCartPopUp({
    addOneMore, selectedProduct, product, quantity, layoutOverrides, isOneMoreAdded, isAdding, activePlacementLabel
}) {
    const cartDataSource = useSelector((state) => state.CartReducer.cartTotals);
    const amastyCartSellingOptionSel = useSelector((state) => state.ConfigReducer.amasty_cart_selling_option);
    const pageType = useSelector((state) => state.UrlRewritesReducer.pageType);
    const pdpVariantOptionsGroups = useSelector(
        (state) => state.ConfigReducer.pdp_variant_options_groups || ''
    ).split(',').map((group) => group.trim());
    const [hasLinkedProducts, setHasLinkedProducts] = useState(false);

    const [showPopUp, setShowPopUp] = useState(false);
    const {
        price_range,
        dynamic_price,
        type,
        options,
        name,
        image: { url } = {}
    } = selectedProduct || {};
    const { price_range: priceRange, dynamic_price: dynamicPrice} = product || {};

    useEffect(() => {
        if (isAdding) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setShowPopUp(true);
        }
    }, [isAdding]);
    const productPrice = fromCache(getPrice, [price_range, dynamic_price, {}, type, options]);

    const finalPriceRange = price_range || priceRange;
    const finalDynamicPrice = dynamic_price || dynamicPrice;
    const { price: { discount: { percentOff } = {} } = {} } = 
        getPrice(finalPriceRange, finalDynamicPrice, {}, type) || {};

    const discountLabel = percentOff ? `-${Math.round(percentOff)}%` : '';

    const getAllOptionLabels = () => {
        const { configurable_options = {}, variants = [], attributes: productAttributes = {} } = product;
        const { attributes: selectedAttributes = {} } = selectedProduct || {};

        const toPosition = (position) => {
            const parsed = Number(position);
            return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
        };

        // Configurable options (e.g. color, size chosen by the user)
        const variantIndex = Object.keys(variants).find((i) => variants[i].sku === selectedProduct.sku);
        const { attributes: variantAttrs = {} } = variants.length ? variants[variantIndex || 0] : {};

        const configurableItems = Object.entries(variantAttrs)
            .filter(([key, { attribute_value }]) => configurable_options[key] && attribute_value !== null)
            .map(([key, { attribute_value }]) => {
                const { attribute_label, attribute_options = {},
                 attribute_display_position } = configurable_options[key];
                const label = attribute_options[attribute_value]?.label;
                return label
                    ? { label: `${attribute_label}: ${label}`, position: toPosition(attribute_display_position) }
                    : null;
            })
            .filter(Boolean);

        // Enriched attributes from pdpVariantOptionsGroups (e.g. ring size, material)
        const existingAttributeIds = new Set(Object.values(configurable_options).map((opt) => opt.attribute_id));
        const finalAttributes = { ...productAttributes, ...selectedAttributes };

        const enrichedItems = Object.values(finalAttributes)
            .filter((attr) => pdpVariantOptionsGroups.includes(attr.attribute_display_group)
                && !existingAttributeIds.has(attr.attribute_id)
                && attr.attribute_value)
            .map(({ attribute_label, attribute_options = {}, attribute_value, attribute_display_position }) => {
                const label = attribute_options[attribute_value]?.label || attribute_value;
                return label
                    ? { label: `${attribute_label}: ${label}`, position: toPosition(attribute_display_position) }
                    : null;
            })
            .filter(Boolean);

        return [...configurableItems, ...enrichedItems]
            .sort((a, b) => a.position - b.position)
            .map(({ label }) => label);
    };

    const renderOptionsList = () => {

        if (product?.type_id === PRODUCT_TYPE.bundle ) {
            return null
        }

        return (
            <ul className="text-bmText-default mt-3 text-p14 ml-0">
                { activePlacementLabel && (
                    <li>
                        <span>
                            { __('Piercing:') }
                            { ' ' }
                        </span>
                        <span>
                            { activePlacementLabel }
                        </span>
                    </li>
                ) }
                { getAllOptionLabels().map((option) => (
                    <li key={ option }>
                        { option }
                    </li>
                )) }
            </ul>
        )
    };

    const blockTypeMap = useMemo(() => ({ crosssell: CROSS_SELL, related: RELATED }), []);
    const { items = [] } = cartDataSource || {};
    const { attributes = {} } = product || {};

    const addedItems = items.find((item) => item.sku === selectedProduct.sku) || [];
    const showOneMore = addedItems?.qty === 1;
    const isEnabled = attributes[SHOW_PAIR_OPTION]?.attribute_value === '1';
    const addOneMoreContent = isOneMoreAdded ? (
        <p className="font-bold text-p14">
            { __('Added') }
        </p>
    ) : (
        <Button
            textSize={ TEXT_SIZE_SMALL }
            className="!font-bold"
            onClick={ addOneMore }
            text={ __('Add 1 more') }
            type={ TYPE_TEXT }
            isLoading={ isAdding }
            width={ WIDTH_HUG }
            size={ SIZE_NONE }
        />
    );

    const infoProductClass = hasLinkedProducts ? 'lg:max-w-[250px] w-screen' : 'lg:max-w-[340px] w-screen';
    const renderProduct = () => (
       <div>
            { (isEnabled && (showOneMore || isOneMoreAdded)) && (
                <div className="flex justify-between py-6 px-4 bg-bmBg-muted mb-4">
                    <p className="text-p14">{ __('Make it a pair by adding one more') }</p>
                    { addOneMoreContent }
                </div>
            ) }
            <div className="flex mb-4">
                <div className=" relative mr-4">
                    <span className="absolute z-10 -top-1 -right-1 rounded-xl bg-neutral-500
                        text-p12 w-5 h-5 justify-around items-center flex p-1 text-bmText-inverted font-bold"
                    >
                        { isOneMoreAdded ? 2 : quantity }
                    </span>
                    <Image
                        plainImageContainerClass="!h-[120px] max-w-[96px] w-screen"
                        isBackground
                        imageCustomClass="object-contain"
                        isPlain
                        ratio="custom"
                        src={ url }
                        className="absolute top-0 w-full h-full"
                    />
                    <LabelContainer position={ TOP_LC_S }>
                        <Label 
                            text={ discountLabel }
                            size={ PADDING_LARGE }
                            color={ COLOR_WHITE_AND_RED } 
                            product={ product } 
                            isFontBold
                        />
                    </LabelContainer>
                </div>
                <div className={ `flex flex-col truncate ${infoProductClass}` }>
                    <p className="mb-1 text-left text-p14 truncate">
                        { name }
                    </p>
                    <ProductPrice
                        price={ productPrice }
                        className="text-p14 font-medium flex gap-1"
                    />
                    { renderOptionsList() }
                </div>
            </div>
            <div className="mb-4">
                <Button
                    isLink
                    goTo="/cart"
                    type={ TYPE_TERTIARY }
                    text={ __('View cart') }
                    width={ WIDTH_FULL }
                />
            </div>
            <FreeShippingNotification className="py-6 mb-6 lg:mb-0" />
       </div>
    );

    const renderProductLinks = () => (
        <div>
            { (amastyCartSellingOptionSel?.block_type === 'cms_block') && (
                // eslint-disable-next-line react/no-danger
                <div dangerouslySetInnerHTML={ { __html: amastyCartSellingOptionSel.cms_block } } />
            ) }
            { (amastyCartSellingOptionSel?.block_type !== 'cms_block') && (
               <div className="lg:max-w-[385px]">
                     <ProductLinks
                         linkType={ blockTypeMap[amastyCartSellingOptionSel?.block_type] || null }
                         title={ __(amastyCartSellingOptionSel?.block_title) }
                         areDetailsLoaded
                         isProductLink
                         setHasLink={ setHasLinkedProducts }
                         gtmEvents={ {
                        clickEvent: EVENT_GTM_PRODUCTCLICK_POPUP,
                        scrollEvent: EVENT_GTM_SCROLL_POPUP
                    } }
                         isPopup
                         isGrid
                         isAddToCartClass
                         renderProductLinksBefore={ layoutOverrides.renderProductLinksBefore }
                     />
               </div>
            ) }
        </div>
    );

    const productLinks = renderProductLinks(renderProductLinks);
    const wrapperClass = 'flex flex-col lg:flex-row';

    if (!showPopUp || pageType === PAGE_TYPE_CART) {
        return null;
    }

    return (
        <Popup
            icon={ CheckMarkFilledIcon }
            clssWidthPopup="max-w-490 lg:max-w-none"
            onClose={ () => setShowPopUp(false) }
            isContainer
            headerPosition="!static"
            id={ ADDED_PRODUCT_POPUP_ID }
            isGoBack={ false }
        >
            <div className={ wrapperClass }>
                { layoutOverrides.renderProduct(renderProduct) }
                { productLinks }
            </div>
        </Popup>
    );
}

AddToCartPopUp.propTypes = {
    addOneMore: PropTypes.func.isRequired,
    selectedProduct: ProductType.isRequired,
    product: ProductType.isRequired,
    quantity: PropTypes.number.isRequired,
    variantIndex: PropTypes.number.isRequired,
    layoutOverrides: PropTypes.shape({
        renderProduct: PropTypes.func,
        renderProductLinksBefore: PropTypes.func
    }).isRequired,
    isOneMoreAdded: PropTypes.bool.isRequired,
    isAdding: PropTypes.bool.isRequired,
    activePlacementLabel: PropTypes.string
};

const AddToCartPopUpWrapped = withLayoutPlugins('AddToCartPopUp')(AddToCartPopUp);

export default AddToCartPopUpWrapped;
